import {
  Grid,
  LinearProgress,
  SvgIcon,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  FormHelperText,
  Autocomplete,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import toast from "../../../../helpers/toast";
import { StyledPaper } from "../../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../customComponents/styled/styledTable";
import { StyledTextField } from "../../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../../customComponents/styled/styledButtons";
import CustomWarningDialog from "../../../customComponents/CustomWarningDialog";
import {
  useGetDepartmentQuery,
  useCreateDepartmentMutation,
  useDeleteDepartmentMutation,
} from "./departmentsApiSlice";
import { useGetTeachersPaginatedQuery } from "../../staff/staffApiSlice";
import EditDepartmentForm from "./EditDepartmentForm";

const departmentFormValidation = yup.object({
  name: yup.string().required("Required"),
  head_title: yup.string().required("Required"),
  head_id: yup.mixed().required("Required"),
});

function Departments() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: departmentData, isLoading: departmentLoading } =
    useGetDepartmentQuery("all");
  const [createDepartment, { isLoading: creating }] =
    useCreateDepartmentMutation();
  const [deleteDepartment] = useDeleteDepartmentMutation();

  const [editDepartment, setEditDepartment] = React.useState(false);
  const [focusedDepartment, setFocusedDepartment] = React.useState(null);
  const [itemToDelete, setItemToDelete] = React.useState(null);
  const [openWarning, setOpenWarning] = React.useState(false);
  const [teacherSearch, setTeacherSearch] = React.useState("");

  const { data: teachersData } = useGetTeachersPaginatedQuery({
    page: 1,
    per_page: 10,
    q: teacherSearch,
  });
  const teachers = teachersData?.teachers ?? [];

  const departmentForm = useFormik({
    initialValues: {
      name: "",
      head_title: "",
      head_id: 0,
    },
    enableReinitialize: true,
    validationSchema: departmentFormValidation,
    onSubmit: async (values) => {
      try {
        const response = await createDepartment(values).unwrap();
        if (response.id) {
          toast.success("Department created successfully");
          departmentForm.resetForm();
        } else {
          toast.error("Department creation failed");
        }
      } catch (error) {
        toast.error(error?.message || "Something went wrong");
      }
    },
  });

  const handleDelete = async ({ fn, item }) => {
    try {
      const response = await fn(item).unwrap();
      if (response.message) {
        toast.success(response.message);
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  return (
    <>
      <CustomWarningDialog
        open={openWarning}
        setOpen={setOpenWarning}
        item={itemToDelete}
        action={handleDelete}
        message="Are you sure you want to delete this department?"
      />

      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={4}>
          <StyledPaper>
            {editDepartment ? (
              <EditDepartmentForm
                item={focusedDepartment}
                setFocusedItem={setFocusedDepartment}
                setEditDepartment={setEditDepartment}
              />
            ) : (
              <>
                <Box className="paperHeader">
                  <Typography variant="formHeading">Setup Department</Typography>
                </Box>
                <Box className="paperBody">
                  <form onSubmit={departmentForm.handleSubmit}>
                    <Grid container columnSpacing={1} rowSpacing={2}>
                      <Grid item xs={12}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            Department Name
                          </Typography>
                          <StyledTextField
                            id="name"
                            name="name"
                            value={departmentForm.values.name}
                            onChange={departmentForm.handleChange}
                            error={
                              departmentForm.touched.name &&
                              Boolean(departmentForm.errors.name)
                            }
                          />
                          <FormHelperText variant="error">
                            {departmentForm.touched.name &&
                              departmentForm.errors.name}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl error fullWidth>
                          <Typography variant="formSubHeading">
                            HEAD'S TITLE (E.G: HEAD OF DEPARTMENT)
                          </Typography>
                          <StyledTextField
                            id="head_title"
                            name="head_title"
                            value={departmentForm.values.head_title}
                            onChange={departmentForm.handleChange}
                            error={
                              departmentForm.touched.head_title &&
                              Boolean(departmentForm.errors.head_title)
                            }
                          />
                          <FormHelperText variant="error">
                            {departmentForm.touched.head_title &&
                              departmentForm.errors.head_title}
                          </FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Typography variant="formSubHeading">
                            SELECT STAFF
                          </Typography>
                          <Autocomplete
                            options={teachers}
                            getOptionLabel={(option) =>
                              option
                                ? `${option.first_name} ${option.last_name}`
                                : ""
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.user_id === value.user_id
                            }
                            value={
                              teachers.find(
                                (t) =>
                                  t.user_id === departmentForm.values.head_id
                              ) || null
                            }
                            onChange={(_, value) =>
                              departmentForm.setFieldValue(
                                "head_id",
                                value?.user_id || ""
                              )
                            }
                            onInputChange={(_, value, reason) => {
                              if (reason === "input") setTeacherSearch(value);
                            }}
                            filterOptions={(x) => x}
                            renderInput={(params) => (
                              <StyledTextField
                                {...params}
                                placeholder="Search staff..."
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} textAlign="right">
                        <StyledLoadingButton loading={creating} type="submit">
                          submit
                        </StyledLoadingButton>
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </>
            )}
          </StyledPaper>
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          onClick={() => {
            setEditDepartment(false);
            setFocusedDepartment(null);
          }}
        >
          <StyledPaper>
            {departmentLoading && <LinearProgress />}
            <Box className="paperHeader">
              <Typography variant="formHeading">Departments</Typography>
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {departmentData?.map((department) => (
                  <Box key={department.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Typography
                      sx={{ color: "primary.main", fontWeight: 600 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFocusedDepartment(department);
                      }}
                    >
                      {department.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Head title: {department.head_title || "-"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Department head: {department?.head?.profile?.first_name || ""} {department?.head?.profile?.last_name || ""}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <SvgIcon
                        sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                        component={EditIcon}
                        inheritViewBox
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusedDepartment(department);
                          setEditDepartment(true);
                        }}
                      />
                      <DeleteIcon
                        className="tableCellLink"
                        onClick={() => {
                          setItemToDelete({ item: department, fn: deleteDepartment });
                          setOpenWarning(true);
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ pb: 4 }}>
                <Table sx={{ width: "100%", boxShadow: "none" }}>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Department</StyledTableCell>
                      <StyledTableCell align="left">Head Title</StyledTableCell>
                      <StyledTableCell align="left">Department Head</StyledTableCell>
                      <StyledTableCell>Edit</StyledTableCell>
                      <StyledTableCell align="right">Delete</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentData?.map((department) => (
                      <StyledTableRow key={department.id}>
                        <StyledTableCell component="th" scope="row">
                          <Box
                            className="tableCellLink"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFocusedDepartment(department);
                            }}
                          >
                            {department.name}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {department.head_title}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          {department?.head?.profile?.first_name} {" "}
                          {department?.head?.profile?.last_name}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <SvgIcon
                            sx={{ color: "primary.safeOnLight", cursor: "pointer" }}
                            component={EditIcon}
                            inheritViewBox
                            onClick={(e) => {
                              e.stopPropagation();
                              setFocusedDepartment(department);
                              setEditDepartment(true);
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <DeleteIcon
                            className="tableCellLink"
                            onClick={() => {
                              setItemToDelete({
                                item: department,
                                fn: deleteDepartment,
                              });
                              setOpenWarning(true);
                            }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </>
  );
}

export default Departments;
